'use client'

import React, { Fragment } from 'react'
import Link from 'next/link'
import { usePathname } from 'next/navigation'
import { cn } from '@/lib/utils'

type BreadcrumbItem = {
  slug: string;
  name: string;
}

type BreadcrumbList = BreadcrumbItem[];

type BreadcrumbVariant = 'default' | 'dark' | 'light'

interface BreadcrumbProps {
  titleCustom?: string
  descriptionCustom?: string
  variant?: BreadcrumbVariant
  breadcrumbMap?: BreadcrumbList
  hiddenPaths?: string[]
  isIndustryView?: boolean
  isCustomPath?: boolean
  showTitle?: boolean
}

const formatSlugToName = (slug: string): string => {
  return slug.replace(/-/g, ' ').replace(/\b\w/g, char => char.toUpperCase())
}

const getStylesByVariant = (variant: BreadcrumbVariant) => ({
  list: cn(
    'hover:text-yellow02 font-thin',
    {
      'text-gray02': variant === 'dark',
      'text-black': variant === 'light'
    }
  ),
  title: cn(
    'text-xl sm:text-2xl md:text-3xl lg:text-4xl font-bold uppercase leading-tight m-0',
    'line-clamp-2',
    {
      'text-white': variant === 'dark',
      'text-dark02': variant === 'light'
    }
  ),
  description: cn(
    'text-justify align-middle text-sm md:text-base',
    {
      'text-gray02': variant === 'dark',
      'text-dark02': variant === 'light'
    }
  )
})

export default function NextBreadcrumb({
  titleCustom,
  descriptionCustom,
  variant = 'default',
  breadcrumbMap = [],
  hiddenPaths = [],
  isIndustryView = false,
  isCustomPath = false,
  showTitle = true,
}: BreadcrumbProps) {
  const pathname = usePathname()
  const pathNames = pathname.split('/').filter(Boolean)
 
  const rawItems: BreadcrumbList = (breadcrumbMap && breadcrumbMap.length > 0)
    ? breadcrumbMap
    : pathNames.map(slug => ({ slug, name: formatSlugToName(slug) }))

  // 2. Filtrar paths ocultos si existen
  const visibleItems = rawItems.filter(item => !hiddenPaths.includes(item.slug))

  const styles = getStylesByVariant(variant)
  
  // El título es el nombre del último elemento de la lista
  const title = titleCustom ?? visibleItems[visibleItems.length - 1]?.name ?? ''

  // Helper simple para generar el HREF acumulando los slugs hasta el índice actual
  const getHref = (index: number) => {
    const slugs = visibleItems.slice(0, index + 1).map(i => i.slug)
    return `/${slugs.join('/')}`
  }

  return (
    <section className={` ${!showTitle ? 'h-10 md:h-28 mt-8 lg:mt-0' : 'h-auto py-8 sm:h-42'} lg:h-32 overflow-hidden flex flex-col justify-center`}> 
      <div className="flex flex-col gap-2">
        <div className="flex flex-col md:flex-row md:flex-wrap justify-between items-start md:items-center">
          <div className="max-w-full md:max-w-[60%]">
            {showTitle && (
              <h1 className={cn(styles.title, "break-words mb-2 md:mb-0")}>{title}</h1>
            )}
          </div>
          
          <ul className="flex flex-wrap items-center text-sm md:text-base uppercase w-full md:w-auto">
            <li className={styles.list}>
              <Link href="/">Inicio</Link>
            </li>
            
            {visibleItems.map((item, index) => {
              const isLastItem = index === visibleItems.length - 1
              if (!isCustomPath && isLastItem) return null

              const href = getHref(index)

              return (
                <React.Fragment key={`breadcrumb-${index}-${item.slug}`}>
                  <span className={cn('mx-1 md:mx-2 flex-shrink-0', variant === 'dark' ? 'text-gray02' : '')}>/</span>
                  <li className={cn(styles.list, "truncate max-w-[200px] md:max-w-[250px] lg:max-w-[300px]")}>
                    <Link href={href} className={cn(styles.list, "truncate block")} title={item.name}>
                      {item.name}
                    </Link>
                  </li>
                </React.Fragment>
              )
            })}
          </ul>
        </div>
        
        {descriptionCustom && (
          <div className="flex w-full pt-2">
            <p className={styles.description}>{descriptionCustom}</p>
          </div>
        )}
      </div>
    </section>
  )
}
